/**
 * The RESOLVED per-model answer for one customer (redesign phase 3, item B5).
 *
 * ── Why a resolved view rather than three lists ─────────────────────────
 *
 * Model access resolves through three layers with three DIFFERENT
 * visibilities, and that difference is the design:
 *
 *   platform  → the model is HIDDEN; the customer cannot act on it
 *   plan      → the model is an UPSELL; buying the plan unlocks it
 *   org       → the model is RESTRICTED by the customer's own admin
 *
 * The console only ever showed the first. "Why can't this customer use model X"
 * was unanswerable from the platform side — and because the org layer runs LAST
 * and wins over the plan layer, a model can be simultaneously advertised as a
 * paid upgrade and blocked by the customer's own admin. Showing the three
 * inputs and leaving the operator to intersect them is how that gets misread.
 *
 * So this renders `resolveModelAccess`'s OUTPUT. It is the one implementation
 * of the chain, shared with the picker and the enqueue gate, which is what
 * stops the console and the server disagreeing about who may use what.
 */
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from '@papan/i18n'
import { MODEL_CATALOG } from '@papan/types'
import { budgetApi } from '../../client/budget'

interface Denial {
  modelId: string
  layer: 'plan' | 'org'
  planName?: string
  by?: string
  /** D12: the plan layer HID this one instead of upselling it (manual authority). */
  hidden?: boolean
}

export function CustomerModelAccess({ tenantId }: { tenantId: string }) {
  const { t } = useTranslation('core-ui')
  const [visible, setVisible] = useState<string[] | null>(null)
  const [denied, setDenied] = useState<Map<string, Denial>>(new Map())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showAll, setShowAll] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await budgetApi.getTenantModelAccess(tenantId)
      setVisible(res.visible)
      setDenied(new Map(res.denied.map(d => [d.modelId, d as Denial])))
      setError(null)
    } catch (e: any) {
      setError(e?.message || 'Failed to resolve model access')
    } finally {
      setLoading(false)
    }
  }, [tenantId])

  useEffect(() => { load() }, [load])

  if (loading && visible === null) {
    return <div className="themed-text-muted text-sm">{t('common:loading')}</div>
  }
  if (error) return <div className="text-sm text-red-400">{error}</div>
  if (!visible) return null

  const visibleSet = new Set(visible)

  /**
   * The one state resolver, so the summary count and the full list cannot
   * disagree.
   *
   * Four states, four meanings — and the two HIDDEN ones must not be collapsed.
   * Both are absent from the tenant's picker, but an operator asking "why can't
   * they use this?" needs to know which lever to reach for: the tenant
   * allow-list, or the plan. Reading absence from `visible` alone would answer
   * "the platform allow-list" for a model the PLAN excluded — a confident, wrong
   * instruction on the one screen built to answer that question.
   *
   * The plan-hidden case is checked FIRST because it is the exception: the only
   * denial that is also absent from `visible`. It is also why the SUMMARY counts
   * exceptions from this function rather than from `visible.length` — under
   * manual plan authority a plan-hidden model is denied AND absent from
   * `visible`, so counting `visible` alone would under-report the exceptions.
   */
  function stateOf(modelId: string): ModelState {
    const denial = denied.get(modelId)
    if (denial?.layer === 'plan' && denial.hidden) return 'plan-hidden'
    if (!visibleSet.has(modelId)) return 'hidden'
    return denial ? denial.layer : 'usable'
  }

  // Mock models are a QA affordance behind an email allow-list, never something
  // sold — listing them here padded the answer with rows no operator can act on.
  const catalog = MODEL_CATALOG.filter((m) => m.provider !== 'mock')

  const rows = catalog.map((m) => ({ model: m, state: stateOf(m.id) }))
  const exceptions = rows.filter((r) => r.state !== 'usable')
  const usableCount = rows.length - exceptions.length

  return (
    <div>
      {/* ── Say the answer, then the exceptions ────────────────────────────
          This listed every model with "Usable" beside it — ~800px of vertical
          scroll in which every row said the same thing, and the handful that
          did NOT had to be found by eye. The question the section exists to
          answer is "can they use what they need, and if not why", so the count
          IS the answer and the list is the footnote. */}
      <div className="themed-bg-secondary rounded-lg px-3 py-2.5">
        <div className="themed-text text-sm">
          {exceptions.length === 0
            ? t('settings.platform.models.allUsable', {
                defaultValue: 'All {{count}} model usable',
                defaultValue_other: 'All {{count}} models usable',
                count: rows.length,
              })
            : t('settings.platform.models.someUsable', {
                defaultValue: '{{n}} of {{total}} models usable',
                n: usableCount, total: rows.length,
              })}
        </div>
        {exceptions.length > 0 && (
          <ul className="mt-2 space-y-1">
            {exceptions.map(({ model, state }) => (
              <li key={model.id} className="flex items-baseline justify-between gap-3 text-[11px]">
                <span className="themed-text-secondary truncate">{model.label}</span>
                <Badge state={state} denial={denied.get(model.id)} t={t} />
              </li>
            ))}
          </ul>
        )}
        <button
          onClick={() => setShowAll(v => !v)}
          className="mt-2 text-[11px] themed-text-muted hover:themed-text transition-colors"
        >
          {showAll
            ? t('settings.platform.models.hideAll', { defaultValue: 'Hide the full list' })
            : t('settings.platform.models.showAll', { defaultValue: 'Show all models' })}
        </button>
      </div>

      {showAll && (
      <div className="themed-bg-secondary rounded-lg overflow-hidden mt-2">
      {catalog.map((m) => {
        const state = stateOf(m.id)
        const denial = denied.get(m.id)

        return (
          <div
            key={m.id}
            className="px-3 py-2 border-b themed-border last:border-b-0 flex items-baseline justify-between gap-3"
          >
            <div className="min-w-0">
              <span className={`text-sm ${state === 'usable' ? 'themed-text' : 'themed-text-muted'}`}>
                {m.label}
              </span>
              <span className="themed-text-muted text-[11px] font-mono ml-2">{m.id}</span>
            </div>
            <span className="shrink-0 text-[11px]">
              {state === 'usable' && (
                <span className="text-emerald-300">
                  {t('settings.platform.models.usable', { defaultValue: 'Usable' })}
                </span>
              )}
              {state === 'hidden' && (
                <span className="themed-text-muted" title={t('settings.platform.models.hiddenHint', {
                  defaultValue: 'Excluded by the platform allow-list — the tenant never sees it.',
                })}>
                  {t('settings.platform.models.hidden', { defaultValue: 'Hidden' })}
                </span>
              )}
              {state === 'plan-hidden' && (
                <span className="text-amber-300/80" title={t('settings.platform.models.planHiddenHint', {
                  defaultValue:
                    "Excluded by the tenant's plan. Plans are assigned by an operator on this deployment, so there is nothing for them to buy — the model is hidden rather than offered as an upgrade. Change their plan, or the plan's model list in the price book.",
                })}>
                  {t('settings.platform.models.planHidden', {
                    defaultValue: 'Hidden — not on their plan',
                  })}
                </span>
              )}
              {state === 'plan' && (
                <span className="text-amber-300" title={t('settings.platform.models.planHint', {
                  defaultValue: 'Shown to the tenant as an upgrade they can buy.',
                })}>
                  {denial?.planName
                    ? t('settings.platform.models.upsellNamed', {
                        defaultValue: 'Needs {{plan}}', plan: denial.planName,
                      })
                    : t('settings.platform.models.upsell', { defaultValue: 'Needs a higher plan' })}
                </span>
              )}
              {state === 'org' && (
                <span className="text-blue-300" title={t('settings.platform.models.orgHint', {
                  defaultValue:
                    "Blocked by the tenant's own admin. This wins over the plan layer, so buying an upgrade would NOT unlock it.",
                })}>
                  {t('settings.platform.models.orgBlocked', {
                    defaultValue: 'Restricted by their admin ({{by}})', by: denial?.by ?? 'org',
                  })}
                </span>
              )}
            </span>
          </div>
        )
      })}
      </div>
      )}
    </div>
  )
}

type ModelState = 'usable' | 'hidden' | 'plan-hidden' | 'plan' | 'org'

/** The exception badge, shared by the summary list and the full list so the two
 *  can never explain the same state differently. */
function Badge({ state, denial, t }: {
  state: ModelState
  denial?: Denial
  t: (k: string, o?: Record<string, unknown>) => string
}) {
  if (state === 'usable') {
    return (
      <span className="text-emerald-300 shrink-0">
        {t('settings.platform.models.usable', { defaultValue: 'Usable' })}
      </span>
    )
  }
  if (state === 'hidden') {
    return (
      <span className="themed-text-muted shrink-0" title={t('settings.platform.models.hiddenHint', {
        defaultValue: 'Excluded by the platform allow-list — the tenant never sees it.',
      })}>
        {t('settings.platform.models.hidden', { defaultValue: 'Hidden' })}
      </span>
    )
  }
  if (state === 'plan-hidden') {
    return (
      <span className="text-amber-300/80 shrink-0" title={t('settings.platform.models.planHiddenHint', {
        defaultValue:
          "Excluded by the tenant's plan. Plans are assigned by an operator on this deployment, so there is nothing for them to buy — the model is hidden rather than offered as an upgrade. Change their plan, or the plan's model list in the price book.",
      })}>
        {t('settings.platform.models.planHidden', { defaultValue: 'Hidden — not on their plan' })}
      </span>
    )
  }
  if (state === 'plan') {
    return (
      <span className="text-amber-300 shrink-0" title={t('settings.platform.models.planHint', {
        defaultValue: 'Shown to the tenant as an upgrade they can buy.',
      })}>
        {denial?.planName
          ? t('settings.platform.models.upsellNamed', { defaultValue: 'Needs {{plan}}', plan: denial.planName })
          : t('settings.platform.models.upsell', { defaultValue: 'Needs a higher plan' })}
      </span>
    )
  }
  return (
    <span className="text-blue-300 shrink-0" title={t('settings.platform.models.orgHint', {
      defaultValue:
        "Blocked by the tenant's own admin. This wins over the plan layer, so buying an upgrade would NOT unlock it.",
    })}>
      {t('settings.platform.models.orgBlocked', {
        defaultValue: 'Restricted by their admin ({{by}})', by: denial?.by ?? 'org',
      })}
    </span>
  )
}
